import { createReducer } from 'redux-starter-kit';
import Notice from '../../modules/utils/Notice';
import i18next from 'i18next';
import { handleError } from '../../modules/utils/handleError';
import { MyReportsApi } from './MyReportsApi';
import { NCALayerSign } from '../../components/NCALayer/NCALayerSign';
import _ from 'lodash';
import { translation } from '../../_helpers/Translate';

export const myReportsModule = 'myReports';

const SET_DATA = `${myReportsModule}/SET_DATA`;
const LOADING = `${myReportsModule}/LOADING`;
const CLEAR_STATE = `${myReportsModule}/CLEAR_STATE`;
const SET_CHILD_DATA_REPORT = `${myReportsModule}/SET_CHILD_DATA_REPORT`;
const RESET_CHILD_DATA_REPORT = `${myReportsModule}/RESET_CHILD_DATA_REPORT`;
const SET_HISTORY_DATA_REPORT = `${myReportsModule}/SET_HISTORY_DATA_REPORT`;
const LOADING_CHILD_REPORT = `${myReportsModule}/LOADING_CHILD_REPORT`;
const SET_CHILD_DDO_REPORT = `${myReportsModule}/SET_CHILD_DDO_REPORT`;
const RESET_HISTORY_DATA_REPORT = `${myReportsModule}/RESET_HISTORY_DATA_REPORT`;

const initialState = {
  loading: true,
  isChildDataReportLoading: false,
  data: [],
  childDataReport: [],
  historyDataReport: [],
  childDdoData: [],
  pageNumber: 1,
  totalElements: 0,
  historyPageNumber: 1,
  historyTotalElements: 0
};

export default createReducer(initialState, {
  [SET_DATA]: (state, action) => {
    state.data = action.data;
    state.pageNumber = action.pageNumber;
    state.totalElements =
      action.pageNumber === 1 ? action.totalElements : state.totalElements;
  },
  [SET_CHILD_DATA_REPORT]: (state, action) => {
    state.childDataReport = action.data;
    state.pageNumber = action.pageNumber;
    state.totalElements =
      action.pageNumber === 1 ? action.totalElements : state.totalElements;
  },
  [SET_HISTORY_DATA_REPORT]: (state, action) => {
    state.historyDataReport = action.data;
    state.historyPageNumber = action.pageNumber;
    state.historyTotalElements =
      action.pageNumber === 1 ? action.totalElements : state.historyTotalElements;
  },
  [SET_CHILD_DDO_REPORT]: (state, action) => {
    state.childDdoData = action.data;
    state.pageNumber = action.pageNumber;
    state.totalElements =
      action.pageNumber === 1 ? action.totalElements : state.totalElements;
  },
  [LOADING]: (state, action) => {
    state.loading = action.loading;
  },
  [LOADING_CHILD_REPORT]: (state, action) => {
    state.isChildDataReportLoading = action.loading;
  },
  [RESET_CHILD_DATA_REPORT]: state => {
    state.childDataReport = [];
  },
  [RESET_HISTORY_DATA_REPORT]: state => {
    state.historyDataReport = [];
    state.historyPageNumber = 1;
    state.historyTotalElements = 0;
  },
  [CLEAR_STATE]: () => initialState
});

export const loadMyChildrenList = filter => async dispatch => {
  dispatch({ type: LOADING, loading: true });
  try {
    const { data } = await MyReportsApi.getMyChildrenListFromIntegration();
    if (Array.isArray(data?.result) && data.result.length > 0) {
      dispatch({
        type: SET_DATA,
        data: data.result,
        pageNumber: filter?.pageNumber || 1,
        totalElements: data.result.length
      });
    } else {
      Notice.info(i18next.t('myReports_noData'));
    }
    return data.result;
  } catch (error) {
    handleError(error, i18next.t('myReports_errorLoad'));
    throw error;
  } finally {
    dispatch({ type: LOADING, loading: false });
  }
};

export const loadMyChildrenDataReport = filter => async dispatch => {
  dispatch({ type: LOADING_CHILD_REPORT, loading: true });
  try {
    const { data } = await MyReportsApi.loadDataReport(JSON.stringify(filter));

    const reportContent = data?.result?.content || [];
    const pageNumber = data?.result?.pageNumber || 1;
    const totalElements = data?.result?.totalElements || 0;

    if (reportContent.length > 0) {
      dispatch({
        type: SET_CHILD_DATA_REPORT,
        data: reportContent,
        pageNumber,
        totalElements
      });
    } else {
      dispatch({
        type: SET_CHILD_DATA_REPORT,
        data: [],
        pageNumber: 1,
        totalElements: 0
      });
      Notice.info(i18next.t('myReports_noData'));
    }
  } catch (error) {
    handleError(error, i18next.t('myReports_errorLoad'));
  } finally {
    dispatch({ type: LOADING_CHILD_REPORT, loading: false });
  }
};

export const confirmOrRejectAttendance = (
  childId,
  consentDate,
  isConsentProvided,
  decisionMonth,
  denialReason,
  filterForReload
) => async dispatch => {
  try {
    dispatch({ type: LOADING, loading: true });
    const { data } = await MyReportsApi.getSignStringForAttendance({
      childId: childId,
      consentDate: consentDate,
      isConsentProvided: isConsentProvided,
      decisionMonth: decisionMonth,
      denialReason: denialReason
    });
    const signature = await NCALayerSign(data.result);
    if (signature) {
      await MyReportsApi.confirmOrRejectAttendance({
        childId: childId,
        consentDate: consentDate,
        isConsentProvided: isConsentProvided,
        decisionMonth: decisionMonth,
        denialReason: denialReason,
        signature
      });
    }
    if (filterForReload) {
      dispatch(loadMyChildrenDataReport(filterForReload));
    }
  } catch (e) {
    const errorCode = _.get(e, 'response.data.errors[0].code');
    if (errorCode === 'error.runtime_user_must_sing_own_signature') {
      Notice.error(translation('must_sing_own_signature'));
    } else {
      handleError(e, translation('myReports_errorConfirmOrReject'));
    }
  } finally {
    dispatch({ type: LOADING, loading: false });
  }
};

export const loadHistoryDataReport = filter => async dispatch => {
  dispatch({ type: LOADING, loading: true });
  try {
    const { data } = await MyReportsApi.loadHistoryDataReport(JSON.stringify(filter));
    const historyContent = data?.content || [];
    const pageNumber = data?.pageNumber || 1;
    const totalElements = data?.totalElements || 0;

    if (historyContent.length > 0) {
      dispatch({
        type: SET_HISTORY_DATA_REPORT,
        data: historyContent,
        pageNumber,
        totalElements
      });
    } else {
      Notice.info(i18next.t('myReports_noData'));
    }
  } catch (error) {
    handleError(error, i18next.t('myReports_errorLoad'));
  } finally {
    dispatch({ type: LOADING, loading: false });
  }
};

export const loadChildDdoDataReport = filter => async dispatch => {
  dispatch({ type: LOADING, loading: true });
  try {
    const { data } = await MyReportsApi.loadChildDdoReport(JSON.stringify(filter));
    const childDdoData = data?.result?.content || [];
    const pageNumber = data?.result?.pageNumber || 1;
    const totalElements = data?.result?.totalElements || 0;

    if (childDdoData.length > 0) {
      dispatch({
        type: SET_CHILD_DDO_REPORT,
        data: childDdoData,
        pageNumber,
        totalElements
      });
    } else {
      Notice.info(i18next.t('myReports_noData'));
    }
  } catch (error) {
    handleError(error, i18next.t('myReports_errorLoad'));
  } finally {
    dispatch({ type: LOADING, loading: false });
  }
};

export const resetChildDataReport = () => ({ type: RESET_CHILD_DATA_REPORT });
// export const loadingChildReport = (loading) => ({ type: LOADING_CHILD_REPORT, loading });
export const resetHistoryDataReport = () => ({ type: RESET_HISTORY_DATA_REPORT });
